import React from 'react';
import { action, observable } from 'mobx';
import { FormattedMessage } from 'react-intl';
import { SHORT_PLAIN_TEXT_LENGTH, MAX_ACCESS_CODE_LENGTH, MIN_ACCESS_CODE_LENGTH } from '../../../../constants/common.constants';
import { EMAIL_VALIDATION_REG_EXP } from '../../../../constants/regex.constants';
import { IHubAttendeeListItemDTO, IHubOrganizationDTO } from '../../../../api/event-hubs.dao';
import { AttendeeRegistrationStatus } from '../../../../api/attendees.dao';

export class HubAttendeeFormDataViewModel {
    @observable public organizationId: number;
    @observable public firstName: string = '';
    @observable public lastName: string = '';
    @observable public email: string = '';
    @observable public assistantEmail: string = '';
    @observable public title: string = '';
    @observable public salutation: string = '';
    @observable public phoneNumber: string = '';
    @observable public accessCode: string = '';
    @observable public registrationStatus: AttendeeRegistrationStatus = AttendeeRegistrationStatus.APPROVED;
    @observable public externalAttendeeId: string = '';
    @observable public formChecked: boolean = false;
    @observable public isMailTouched: boolean = false;
    @observable private organizations: IHubOrganizationDTO[] = [];
    @observable private errors: { [key: string]: string } = {};
    @observable private serverErrorMessage: string = '';

    constructor(organizations: IHubOrganizationDTO[]) {
        this.organizations = organizations;
    }

    @action
    public populateFromListItem(attendee: IHubAttendeeListItemDTO): void {
        this.organizationId = attendee.organizationId ?? 0;
        this.firstName = attendee.firstName ?? '';
        this.lastName = attendee.lastName ?? '';
        this.email = attendee.email ?? '';
        this.assistantEmail = attendee.assistantEmail ?? '';
        this.title = attendee.title ?? '';
        this.salutation = attendee.salutation ?? '';
        this.phoneNumber = attendee.phoneNumber ?? '';
        this.registrationStatus = (attendee.registrationStatus as AttendeeRegistrationStatus) ?? AttendeeRegistrationStatus.APPROVED;
        this.accessCode = attendee.accessCode ?? '';
        this.externalAttendeeId = attendee.externalAttendeeId ?? '';
    }

    public getOrganizations(): IHubOrganizationDTO[] {
        return this.organizations;
    }

    @action
    public updateOrganizationsList(newList: IHubOrganizationDTO[]): void {
        this.organizations = newList;
    }

    @action
    public setOrganizationId(id: number): void {
        this.organizationId = id;
    }

    @action
    public setServerErrorMessage(message: string): void {
        this.serverErrorMessage = message;
    }

    public getServerErrorMessage(): string {
        return this.serverErrorMessage;
    }

    @action
    public setFieldError(field: string, message: string): void {
        this.errors[field] = message;
    }

    @action
    public clearAllFieldErrors(): void {
        this.errors = {};
    }

    public hasApiError(field: string): boolean {
        return !!this.errors[field];
    }

    public getFieldError(field: string): JSX.Element | null {
        if (this.errors[field]) {
            return <>{this.errors[field]}</>;
        }
        return null;
    }

    @action
    private clearError(field: string): void {
        if (this.errors[field]) {
            delete this.errors[field];
        }
    }

    @action
    public clearEmailError(): void {
        this.clearError('email');
        this.setServerErrorMessage('');
    }

    @action
    public clearExternalAttendeeIdError(): void {
        this.clearError('externalAttendeeId');
        this.setServerErrorMessage('');
    }

    public getOrganizationError(): string | null {
        return !this.organizationId ? 'Organization is required' : null;
    }

    public shouldShowEmailValidationError(value: string, isRequired: boolean, includeEmailApiError = false): boolean {
        const hasError = this.getEmailError(value, isRequired) || (includeEmailApiError && this.getFieldError('email'));
        return !!(hasError && (this.isMailTouched || this.formChecked));
    }

    public getFirstNameError(): JSX.Element | null {
        if (this.firstName) {
            if (this.firstName.length > SHORT_PLAIN_TEXT_LENGTH) {
                return <FormattedMessage
                    defaultMessage='Too long - max length is 255 characters'
                    id='oe.firstName.tooLong'
                    description='Error message text'
                />;
            }
            return null;
        }
        return <FormattedMessage
            defaultMessage='First Name is required'
            id='oe.user.userFirstNameIsRequired'
            description='Error message text'
        />;
    }

    public getLastNameError(): JSX.Element | null {
        if (this.lastName) {
            if (this.lastName.length > SHORT_PLAIN_TEXT_LENGTH) {
                return <FormattedMessage
                    id='oe.lastName.tooLong'
                    defaultMessage='Too long - max length is 255 characters'
                    description='Error message text'
                />;
            }
            return null;
        }
        return <FormattedMessage
            defaultMessage='Last Name is required'
            id='oe.user.userLastNameIsRequired'
            description='Error message text'
        />;
    }

    public getEmailError(value: string, isRequired = false): JSX.Element | null {
        if (value) {
            return EMAIL_VALIDATION_REG_EXP.test(value) ? null
                : <FormattedMessage
                    id='oe.email.emailIsNotValid'
                    defaultMessage='Must be a valid email address'
                    description='Error message text'
                />;
        }
        if (isRequired) {
            return <FormattedMessage
                id='oe.email.emailIsRequired'
                defaultMessage='Email is required'
                description='Error message text'
            />;
        }
        return null;
    }

    public getTitleError(): JSX.Element | null {
        if (this.title && this.title.length > SHORT_PLAIN_TEXT_LENGTH) {
            return <FormattedMessage
                defaultMessage='Title is too long'
                id='oe.title.tooLong'
                description='Error message text'
            />;
        }
        return null;
    }

    public getSalutationError(): JSX.Element | null {
        if (this.salutation && this.salutation.length > SHORT_PLAIN_TEXT_LENGTH) {
            return <FormattedMessage
                defaultMessage='Salutation is too long'
                id='oe.salutation.tooLong'
                description='Error message text'
            />;
        }
        return null;
    }

    public getPhoneNumberError(): JSX.Element | null {
        if (this.phoneNumber && this.phoneNumber.length > SHORT_PLAIN_TEXT_LENGTH) {
            return <FormattedMessage
                defaultMessage='Phone number is too long'
                id='oe.phoneNumber.tooLong'
                description='Error message text'
            />;
        }
        return null;
    }

    public getAccessCodeError(): string | null {
        if (this.accessCode) {
            if (this.accessCode.trim().length < MIN_ACCESS_CODE_LENGTH || this.accessCode.length > MAX_ACCESS_CODE_LENGTH) {
                return `Access code length must be between ${MIN_ACCESS_CODE_LENGTH} and ${MAX_ACCESS_CODE_LENGTH} characters`;
            }
        }
        return null;
    }

    public getExternalAttendeeIdError(): string | null {
        if (this.externalAttendeeId?.length > SHORT_PLAIN_TEXT_LENGTH) {
            return 'External Id is too long';
        }
        return this.errors['externalAttendeeId'] || null;
    }

    @action
    public checkForm(): boolean {
        this.formChecked = true;
        return !this.getOrganizationError()
            && !this.getFirstNameError()
            && !this.getLastNameError()
            && !this.getEmailError(this.email, true)
            && !this.getEmailError(this.assistantEmail)
            && !this.getTitleError()
            && !this.getSalutationError()
            && !this.getPhoneNumberError()
            && !this.getAccessCodeError()
            && !this.getExternalAttendeeIdError();
    }

    public toCreateDTO(): {
        organizationId: number;
        firstName: string;
        lastName: string;
        email: string;
        assistantEmail?: string;
        title?: string;
        salutation?: string;
        phoneNumber?: string;
        accessCode?: string;
        registrationStatus: string;
        externalAttendeeId?: string;
    } {
        return {
            organizationId: this.organizationId,
            firstName: this.firstName?.trim(),
            lastName: this.lastName?.trim(),
            email: this.email?.trim(),
            assistantEmail: this.assistantEmail?.trim() || undefined,
            title: this.title?.trim() || undefined,
            salutation: this.salutation?.trim() || undefined,
            phoneNumber: this.phoneNumber?.trim() || undefined,
            accessCode: this.accessCode?.trim() || undefined,
            registrationStatus: this.registrationStatus,
            externalAttendeeId: this.externalAttendeeId?.trim() || undefined,
        };
    }
}
